import { useEffect, useState } from "react";
import { api, fileUrl } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Download, ExternalLink, FileText, Pencil, Trash2, Eye, ChevronLeft, ChevronRight } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { hasPerm } from "@/lib/permissions";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import OrderDetailModal from "@/components/OrderDetailModal";
import SearchInput from "@/components/SearchInput";

const peso = (n) => `₱${(n || 0).toLocaleString("en-PH", { minimumFractionDigits: 2 })}`;

const COLUMNS = [
  { key: "pending_payment", title: "Pending payment" },
  { key: "paid", title: "Paid" },
  { key: "in_production", title: "In production" },
  { key: "ready_for_pickup", title: "Ready / Shipped" },
  { key: "completed", title: "Completed" },
];

const ROLE_NEXT = {
  super_admin: { pending_payment: "paid", paid: "in_production", in_production: "ready_for_pickup", ready_for_pickup: "completed", shipped: "completed" },
  cashier:     { pending_payment: "paid" },
  production:  { paid: "in_production", in_production: "ready_for_pickup" },
  releasing:   { ready_for_pickup: "completed", shipped: "completed" },
};

const AdminOrdersPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [drafts, setDrafts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [draftSearch, setDraftSearch] = useState("");
  const [detail, setDetail] = useState(null);
  const [pages, setPages] = useState({}); // {column_key: pageNumber}
  const PAGE_SIZE = 4;

  const openDetail = async (orderId) => {
    try {
      const { data } = await api.get(`/orders/${orderId}`);
      setDetail(data);
    } catch { toast.error("Failed to load order"); }
  };

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [o, d] = await Promise.all([
        api.get("/orders"),
        hasPerm(user, "orders.create") ? api.get("/drafts") : Promise.resolve({ data: [] }),
      ]);
      setOrders(o.data); setDrafts(d.data);
    } catch { toast.error("Failed to load orders"); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchAll(); /* eslint-disable-next-line */ }, []);

  const advance = async (order) => {
    const next = ROLE_NEXT[user?.role]?.[order.status];
    if (!next) return toast.error("Your role can't advance this status");
    try {
      await api.patch(`/orders/${order.order_id}/status`, { status: next });
      toast.success(`Moved to ${next.replace(/_/g, " ")}`);
      fetchAll();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const confirmPayment = async (order) => {
    try {
      await api.post("/payments/confirm", { order_id: order.order_id, method: "manual" });
      toast.success("Payment confirmed");
      fetchAll();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const deleteDraft = async (d, e) => {
    e?.stopPropagation();
    if (!window.confirm(`Delete draft for ${d.customer?.name || "—"}?`)) return;
    try {
      await api.delete(`/drafts/${d.draft_id}`);
      toast.success("Draft deleted");
      fetchAll();
    } catch (err) { toast.error("Failed to delete draft"); }
  };

  const filtered = filter === "all" ? orders : orders.filter((o) => o.source === filter);
  const filteredDrafts = drafts.filter((d) => {
    if (!draftSearch) return true;
    const s = draftSearch.toLowerCase();
    return (d.customer?.name || "").toLowerCase().includes(s)
        || (d.customer?.company || "").toLowerCase().includes(s)
        || (d.customer?.email || "").toLowerCase().includes(s);
  }).sort((a, b) => (b.updated_at || "").localeCompare(a.updated_at || ""));

  return (
    <div className="p-6 lg:p-10 pav-fade-in" data-testid="admin-orders-page">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-6">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-[#06B6D4] font-semibold">Orders</p>
          <h1 className="font-display text-3xl font-extrabold text-[#0B1E3F] mt-1">Production board</h1>
        </div>
        <div className="flex items-center gap-2">
          {hasPerm(user, "orders.create") && (
            <Link to="/admin/orders/new" className="btn-accent text-sm inline-flex items-center gap-2" data-testid="new-manual-order-link">
              + New Job Order
            </Link>
          )}
        </div>
      </div>

      <Tabs defaultValue="board">
        <TabsList className="bg-[#F4F6FB] mb-4">
          <TabsTrigger value="board" data-testid="tab-board">Production Board</TabsTrigger>
          {hasPerm(user, "orders.create") && (
            <TabsTrigger value="drafts" data-testid="tab-drafts">Drafts ({drafts.length})</TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="board">
          <div className="flex items-center gap-2 mb-4">
            <div className="flex bg-white border border-[#E2E8F0] rounded-lg p-1">
              {["all", "online", "manual"].map((f) => (
                <button key={f} onClick={() => setFilter(f)}
                  data-testid={`filter-${f}-button`}
                  className={`px-3 py-1.5 rounded-md text-xs font-semibold capitalize transition ${filter === f ? "bg-[#0B1E3F] text-white" : "text-neutral-500 hover:text-[#0B1E3F]"}`}>
                  {f}
                </button>
              ))}
            </div>
          </div>
          {loading ? (
            <div className="p-12 text-center"><Loader2 className="h-8 w-8 animate-spin mx-auto text-[#0B1E3F]" /></div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-4">
              {COLUMNS.map((col) => {
                const cards = filtered.filter((o) => o.status === col.key || (col.key === "ready_for_pickup" && o.status === "shipped"));
                const pageNum = pages[col.key] || 1;
                const totalPages = Math.max(1, Math.ceil(cards.length / PAGE_SIZE));
                const safePage = Math.min(pageNum, totalPages);
                const start = (safePage - 1) * PAGE_SIZE;
                const visibleCards = cards.slice(start, start + PAGE_SIZE);
                return (
                  <div key={col.key} className="kanban-col" data-testid={`kanban-col-${col.key}`}>
                    <div className="flex items-center justify-between px-2 py-2 mb-2">
                      <h3 className="font-display font-bold text-sm text-[#0B1E3F]">{col.title}</h3>
                      <span className="text-xs bg-white px-2 py-0.5 rounded-full border border-[#E2E8F0]">{cards.length}</span>
                    </div>
                    <div className="space-y-2">
                      {visibleCards.map((o) => {
                        const next = ROLE_NEXT[user?.role]?.[o.status];
                        return (
                          <div key={o.order_id} className="bg-white rounded-xl p-3 border border-[#E2E8F0] shadow-sm hover:bg-[#F8FAFC] cursor-pointer" data-testid={`kanban-card-${o.order_id}`} onClick={() => openDetail(o.order_id)}>
                            <div className="flex justify-between items-start gap-2">
                              <div className="min-w-0">
                                <p className="font-semibold text-sm truncate">{o.order_id}</p>
                                <p className="text-xs text-neutral-500">{o.customer_name}</p>
                              </div>
                              <p className="font-display font-bold text-sm text-[#0B1E3F]">₱{o.total.toFixed(0)}</p>
                            </div>
                            <div className="mt-1 text-xs text-neutral-400">
                              <span className="capitalize">{o.source || "online"}</span> · {o.fulfillment_type}
                              {o.payment_status === "unpaid" && <span className="ml-1 text-red-600">· UNPAID</span>}
                            </div>
                            <div className="mt-2 text-xs text-neutral-500 space-y-0.5">
                              {o.items.map((it, i) => (
                                <p key={i}>{it.width}×{it.height} {it.unit} · {it.finishing?.replace("_", " ")} ×{it.quantity}</p>
                              ))}
                            </div>
                            <div className="mt-2 flex flex-wrap gap-1">
                              {o.items.map((it, i) => it.file_url && (
                                <a key={i} href={fileUrl(it.file_url)} target="_blank" rel="noreferrer"
                                  onClick={(e) => e.stopPropagation()}
                                  className="inline-flex items-center gap-1 text-xs text-[#0B1E3F] hover:underline">
                                  <Download className="h-3 w-3" /> File {i + 1}
                                </a>
                              ))}
                            </div>
                            <div className="mt-3 flex gap-1.5" onClick={(e) => e.stopPropagation()}>
                              {o.status === "pending_payment" && hasPerm(user, "payments.confirm") && (
                                <Link to={`/cashier?order=${o.order_id}`}
                                  data-testid={`confirm-payment-${o.order_id}`}
                                  className="flex-1 text-xs bg-[#06B6D4] text-white px-2 py-1.5 rounded-lg hover:bg-[#0891B2] font-semibold text-center">
                                  Confirm Payment
                                </Link>
                              )}
                              {next && o.status !== "pending_payment" && (
                                <button onClick={() => advance(o)}
                                  data-testid={`advance-${o.order_id}`}
                                  className="flex-1 text-xs bg-[#0B1E3F] text-white px-2 py-1.5 rounded-lg hover:bg-[#172554]">
                                  → {next.replace(/_/g, " ")}
                                </button>
                              )}
                              <button onClick={() => openDetail(o.order_id)}
                                data-testid={`open-detail-${o.order_id}`}
                                className="text-xs border border-[#E2E8F0] px-2 py-1.5 rounded-lg hover:bg-[#F4F6FB]" title="View details">
                                <Eye className="h-3 w-3" />
                              </button>
                            </div>
                          </div>
                        );
                      })}
                      {cards.length === 0 && <p className="text-xs text-neutral-400 text-center py-6">No orders</p>}
                    </div>
                    {totalPages > 1 && (
                      <div className="mt-3 flex items-center justify-between px-1" data-testid={`kanban-pagination-${col.key}`}>
                        <button onClick={() => setPages((p) => ({ ...p, [col.key]: Math.max(1, safePage - 1) }))}
                          disabled={safePage === 1}
                          className="h-7 w-7 rounded-full bg-white border border-[#E2E8F0] flex items-center justify-center hover:bg-[#F4F6FB] disabled:opacity-30 disabled:cursor-not-allowed">
                          <ChevronLeft className="h-3.5 w-3.5" />
                        </button>
                        <p className="text-[11px] text-neutral-500 font-mono">{safePage} / {totalPages}</p>
                        <button onClick={() => setPages((p) => ({ ...p, [col.key]: Math.min(totalPages, safePage + 1) }))}
                          disabled={safePage === totalPages}
                          className="h-7 w-7 rounded-full bg-white border border-[#E2E8F0] flex items-center justify-center hover:bg-[#F4F6FB] disabled:opacity-30 disabled:cursor-not-allowed">
                          <ChevronRight className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </TabsContent>

        {hasPerm(user, "orders.create") && (
          <TabsContent value="drafts">
            <div className="pav-card p-0 overflow-hidden">
              <div className="p-4 border-b border-[#E2E8F0]">
                <SearchInput
                  value={draftSearch}
                  onChange={setDraftSearch}
                  placeholder="Search drafts by customer name, company, email…"
                  data-testid="draft-search-input" />
              </div>
              {loading ? (
                <div className="p-12 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto" /></div>
              ) : filteredDrafts.length === 0 ? (
                <div className="p-12 text-center text-neutral-500">
                  <FileText className="h-10 w-10 mx-auto text-neutral-300 mb-2" />
                  <p>No drafts. Save work-in-progress orders to resume later.</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-[#F4F6FB] text-xs uppercase tracking-wider text-neutral-600">
                      <tr>
                        <th className="text-left px-3 py-2">Last updated</th>
                        <th className="text-left px-3 py-2">Customer</th>
                        <th className="text-left px-3 py-2">Type</th>
                        <th className="text-left px-3 py-2">Services</th>
                        <th className="text-left px-3 py-2">Saved by</th>
                        <th className="text-right px-3 py-2">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[#E2E8F0]">
                      {filteredDrafts.map((d) => (
                        <tr key={d.draft_id} onClick={() => navigate(`/admin/orders/new?draft=${d.draft_id}`)}
                          data-testid={`draft-row-${d.draft_id}`}
                          className="hover:bg-[#F8FAFC] cursor-pointer">
                          <td className="px-3 py-2 text-neutral-600">{d.updated_at ? new Date(d.updated_at).toLocaleString() : "—"}</td>
                          <td className="px-3 py-2">
                            <p className="font-semibold">{d.customer?.name || d.customer?.company || "—"}</p>
                            {d.customer?.email && <p className="text-xs text-neutral-500">{d.customer.email}</p>}
                          </td>
                          <td className="px-3 py-2 capitalize text-neutral-600">{(d.customerType || "—").replace("_", " ")}</td>
                          <td className="px-3 py-2 text-xs text-neutral-600">
                            {(d.services || []).length === 0 ? "—" :
                              (d.services || []).map((s, i) => <p key={i}>{s.service_name} ×{s.quantity}</p>)}
                          </td>
                          <td className="px-3 py-2 text-neutral-600">{d.saved_by || "—"}</td>
                          <td className="px-3 py-2 text-right whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                            <Link to={`/admin/orders/new?draft=${d.draft_id}`}
                              data-testid={`draft-resume-${d.draft_id}`}
                              className="p-1.5 hover:bg-[#F4F6FB] rounded inline-block" title="Resume">
                              <Pencil className="h-4 w-4 text-[#0B1E3F]" />
                            </Link>
                            <button onClick={(e) => deleteDraft(d, e)}
                              data-testid={`draft-delete-${d.draft_id}`}
                              className="p-1.5 hover:bg-red-50 rounded" title="Delete draft">
                              <Trash2 className="h-4 w-4 text-red-600" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </TabsContent>
        )}
      </Tabs>

      <OrderDetailModal order={detail} onClose={() => setDetail(null)}
        actions={detail && (
          <>
            {detail.status === "pending_payment" && hasPerm(user, "payments.confirm") && (
              <Link to={`/cashier?order=${detail.order_id}`} onClick={() => setDetail(null)}
                data-testid="modal-confirm-payment-link"
                className="btn-accent text-sm inline-flex items-center gap-2">
                Confirm payment
              </Link>
            )}
            <Link to={`/track?id=${detail.order_id}`} target="_blank" rel="noreferrer"
              className="btn-outline text-sm inline-flex items-center gap-2">
              <ExternalLink className="h-4 w-4" /> Track page
            </Link>
          </>
        )} />
    </div>
  );
};

export default AdminOrdersPage;
